import cn from 'classnames';
import { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { TranscriptorEncounterResponse } from '@/shared/api/transcriptor/transcriptorApi';

import { ApproveDocumentArgs, AttachPatientArgs } from '../../hooks';

import styles from './EncounterDetails.module.scss';
import { useEncounterTabs } from './hooks';
import { getTabActionBarKind } from './lib/getTabActionBarKind';
import { TabActionBar } from './TabActionBar/TabActionBar';
import { EncounterDetailsHeader } from './ui/EncounterDetailsHeader/EncounterDetailsHeader';
import { EncounterTabPanel } from './ui/EncounterTabPanel/EncounterTabPanel';
import { EncounterTabs } from './ui/EncounterTabs/EncounterTabs';
import { PatientLinkRow } from './ui/PatientLinkRow/PatientLinkRow';

type EncounterDetailsProps = {
  encounter: TranscriptorEncounterResponse | null;
  onAttachPatient: (args: AttachPatientArgs) => Promise<void>;
  className?: string;
  error?: string | null;
  loading?: boolean;
  onApproveDocument?: (args: ApproveDocumentArgs) => Promise<void>;
};

export const EncounterDetails: FC<EncounterDetailsProps> = (props) => {
  const {
    className,
    encounter,
    error,
    loading,
    onApproveDocument,
    onAttachPatient,
  } = props;
  const { activeTab, tabs, onTabChange } = useEncounterTabs(encounter);

  const activeDocument =
    activeTab?.kind === 'document' ? activeTab.document : null;
  const activeDocumentId = activeDocument?.id ?? null;
  const isActiveDocumentApproved = Boolean(activeDocument?.approved);

  const handleApprove = useCallback(() => {
    if (!activeDocumentId || !onApproveDocument) {
      return;
    }

    onApproveDocument({
      documentId: activeDocumentId,
      approved: !isActiveDocumentApproved,
    });
  }, [activeDocumentId, isActiveDocumentApproved, onApproveDocument]);

  if (loading) {
    return (
      <div className={cn(styles.empty, className)}>
        <FormattedMessage id="global.loading" defaultMessage="Loading…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(styles.empty, className)}>
        <FormattedMessage
          id="encounters.error"
          defaultMessage="Error: {message}"
          values={{ message: error }}
        />
      </div>
    );
  }

  if (!encounter) {
    return (
      <div className={cn(styles.empty, className)}>
        <FormattedMessage
          id="encounters.details.empty"
          defaultMessage="Select an encounter to see details"
        />
      </div>
    );
  }

  const actionBarKind = getTabActionBarKind(activeTab);
  const canViewEvaluation = Boolean(
    encounter.your_permissions?.can_view_evaluation,
  );

  return (
    <div
      className={cn(styles.container, className)}
      data-section="encounter-details"
    >
      <EncounterDetailsHeader encounter={encounter} />
      <PatientLinkRow encounter={encounter} onAttachPatient={onAttachPatient} />
      <EncounterTabs
        activeTab={activeTab}
        canViewEvaluation={canViewEvaluation}
        evaluationScore={encounter.evaluation_overall_score}
        tabs={tabs}
        onTabChange={onTabChange}
      />

      {actionBarKind === 'document-approvable' && (
        <TabActionBar
          kind={actionBarKind}
          contextLabel={activeTab?.value ?? ''}
          isApproved={isActiveDocumentApproved}
          onApprove={
            activeDocumentId && onApproveDocument ? handleApprove : undefined
          }
        />
      )}

      <div className={styles.tabContent} role="tabpanel">
        {actionBarKind && actionBarKind !== 'document-approvable' && (
          <TabActionBar
            kind={actionBarKind}
            contextLabel={activeTab?.value ?? ''}
            className={styles.floatingActionBar}
          />
        )}
        <EncounterTabPanel
          activeTab={activeTab}
          canViewEvaluation={canViewEvaluation}
          encounter={encounter}
        />
      </div>
    </div>
  );
};
